import React, { useState, useMemo, useCallback } from 'react';
import {
  Lock, Briefcase, Building2, Award, AlertTriangle, CheckCircle2,
  Target, ShieldCheck, FileSignature, Truck, Coins, Clock, Hourglass,
} from 'lucide-react';
import useGameStore from '../store/gameStore';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../store/stableEmpties';
import { useTranslation } from '../i18n';
import {
  getTenderEligibility, computeTenderWinProbability, computeTenderProbabilityRange,
  getCompetitiveIntel,
} from '../store/helpers/tenderHelpers';
import TenderApplicationModal from '../components/tenders/TenderApplicationModal';
import { ContractAssignModal } from '../components/game/ContractAssignModal';
import MarkdownLite from '../components/MarkdownLite';

const TIER_TONE = {
  local:    { bg: 'bg-emerald-500/15', text: 'text-emerald-300', border: 'border-emerald-500/40', labelKey: 'ao_tier_local' },
  regional: { bg: 'bg-sky-500/15',     text: 'text-sky-300',     border: 'border-sky-500/40',     labelKey: 'ao_tier_regional' },
  national: { bg: 'bg-yellow-500/15',  text: 'text-yellow-300',  border: 'border-yellow-500/40',  labelKey: 'ao_tier_national' },
};
// 04/06/2026 — Spot exclusivity tone (deep purple). Distinct from
// classical tier tones to surface its contractual constraints visually.
const SPOT_TONE = {
  bg: 'bg-purple-500/20', text: 'text-purple-200', border: 'border-purple-500/60',
  cardBorder: 'border-purple-500/40',
  cardHover: 'hover:border-purple-400/70',
};

// 02/06/2026 — BUG FIX (React error #185 in prod) : voir docstring de
// src/store/stableEmpties.js. Les fallbacks DOIVENT être des constantes
// stables sinon Zustand boucle infiniment.

/**
 * Appels d'Offres (AO) — premium tenders page.
 *
 * 01/06/2026 — Replaces the old comp-bid modal with a proper subscription
 * funnel. Locked until at least one commercial role is hired (Léa,
 * Marc, or Sylvie).
 *
 * 02/06/2026 — Full FR/EN i18n via translations.js (ao_* keys).
 */
export default function AppelsOffres() {
  const { t } = useTranslation();
  const activeTenders = useGameStore(s => s.activeTenders || EMPTY_ARRAY);
  const activeApplications = useGameStore(s => s.activeApplications || EMPTY_ARRAY);
  const tenderHistory = useGameStore(s => s.tenderHistory || EMPTY_ARRAY);
  const submitTenderOffer = useGameStore(s => s.submitTenderOffer);
  const acceptCounter = useGameStore(s => s.acceptCounterOffer);
  const rejectCounter = useGameStore(s => s.rejectCounterOffer);
  const hiredSupport = useGameStore(s => s.hiredSupport || EMPTY_OBJECT);
  const vehicles = useGameStore(s => s.vehicles || EMPTY_ARRAY);
  const drivers = useGameStore(s => s.drivers || EMPTY_ARRAY);
  const totalDays = useGameStore(s => s.totalDays || 0);
  const ecoVadisHistory = useGameStore(s => s.ecoVadisHistory || EMPTY_ARRAY);
  // 12/02/2026 — Fenêtre "concurrence renforcée" (rétro-effet du narratif
  // presse). Bannière visible tant que competitorAggressionUntilDay > today.
  const competitorAggressionUntilDay = useGameStore(s => s.competitorAggressionUntilDay || 0);
  const aggressionActive = competitorAggressionUntilDay > totalDays;
  const aggressionDaysLeft = Math.max(0, competitorAggressionUntilDay - totalDays);
  const eligibility = useMemo(
    () => getTenderEligibility({ hiredSupport, vehicles, drivers, totalDays, ecoVadisHistory }),
    [hiredSupport, vehicles, drivers, totalDays, ecoVadisHistory],
  );

  const [openTenderId, setOpenTenderId] = useState(null);
  // 15/06/2026 — staffing direct des AO gagnés : les contrats issus d'un
  // AO (tenderWon) encore dans availableContracts s'assignent ici-même
  // (camion + chauffeur) sans repasser par le Dashboard.
  const availableContracts = useGameStore(s => s.availableContracts || EMPTY_ARRAY);
  const [staffTargetId, setStaffTargetId] = useState(null);
  const wonToStaff = useMemo(
    () => availableContracts.filter(c => c.tenderWon),
    [availableContracts],
  );
  const pendingApps = activeApplications.filter(a => a.stage === 'pending');
  const counterApps = activeApplications.filter(a => a.stage === 'counter');
  const staffTarget = staffTargetId
    ? wonToStaff.find(c => c.instanceId === staffTargetId)
    : null;

  const openTender = openTenderId
    ? activeTenders.find(x => x.instanceId === openTenderId)
    : null;

  // Stable handlers passed to the modal. computeProbability and intel both
  // read the latest store state imperatively (the modal's lifetime is short
  // and locked to the currently selected tender).
  const intel = useMemo(
    () => openTender ? getCompetitiveIntel(useGameStore.getState(), openTender) : null,
    [openTender],
  );
  const computeProbability = useCallback(
    (offer) => openTender
      ? computeTenderWinProbability(useGameStore.getState(), openTender, offer)
      : { probability: 0.5, breakdown: {} },
    [openTender],
  );
  const computeProbabilityRange = useCallback(
    (offer) => openTender
      ? computeTenderProbabilityRange(useGameStore.getState(), openTender, offer)
      : { low: 0.5, mid: 0.5, high: 0.5, spread: 0, breakdown: {}, supportFamilies: [] },
    [openTender],
  );
  const handleSubmit = useCallback(
    (offer) => {
      if (!openTender) return { ok: false };
      const res = submitTenderOffer(openTender.instanceId, offer);
      setOpenTenderId(null);
      return res;
    },
    [openTender, submitTenderOffer],
  );
  const handleClose = useCallback(() => setOpenTenderId(null), []);

  if (!eligibility.pageUnlocked) {
    return <LockedView t={t} />;
  }

  return (
    <div className="space-y-6" data-testid="page-appels-offres">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-slate-200 flex items-center gap-2">
            <FileSignature className="w-6 h-6 text-emerald-300" />
            {t('ao_page_title')}
          </h1>
          <p className="text-sm text-slate-400 mt-1 leading-relaxed">
            {t('ao_page_subtitle')}
          </p>
        </div>
        <EligibilitySummary elig={eligibility} t={t} />
      </header>

      {/* 15/06/2026 — récap « AO en cours » : candidatures, contre-offres,
          gagnés à staffer (demande utilisateur). */}
      {(pendingApps.length > 0 || counterApps.length > 0 || wonToStaff.length > 0) && (
        <section className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="tender-pipeline">
          <div className="bg-ink-800 border border-ink-600 rounded-lg px-4 py-3 flex items-center gap-3" data-testid="tender-pipeline-pending">
            <Hourglass className="w-5 h-5 text-sky-300 flex-shrink-0" />
            <div>
              <p className="text-lg font-black font-mono text-foreground">{pendingApps.length}</p>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">{t('ao_pipeline_pending')}</p>
            </div>
          </div>
          <div className={`rounded-lg px-4 py-3 flex items-center gap-3 border ${counterApps.length > 0 ? 'bg-amber-500/10 border-amber-500/40' : 'bg-ink-800 border-ink-600'}`} data-testid="tender-pipeline-counter">
            <AlertTriangle className={`w-5 h-5 flex-shrink-0 ${counterApps.length > 0 ? 'text-amber-300' : 'text-slate-600'}`} />
            <div>
              <p className="text-lg font-black font-mono text-foreground">{counterApps.length}</p>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">{t('ao_pipeline_counter')}</p>
            </div>
          </div>
          <div className={`rounded-lg px-4 py-3 flex items-center gap-3 border ${wonToStaff.length > 0 ? 'bg-emerald-500/10 border-emerald-500/40' : 'bg-ink-800 border-ink-600'}`} data-testid="tender-pipeline-staff">
            <Truck className={`w-5 h-5 flex-shrink-0 ${wonToStaff.length > 0 ? 'text-emerald-300' : 'text-slate-600'}`} />
            <div>
              <p className="text-lg font-black font-mono text-foreground">{wonToStaff.length}</p>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">{t('ao_pipeline_staff')}</p>
            </div>
          </div>
        </section>
      )}

      {/* AO gagnés — association directe camion + chauffeur */}
      {wonToStaff.length > 0 && (
        <section data-testid="tender-staff-section">
          <h2 className="text-sm uppercase tracking-widest text-emerald-300 font-mono mb-3 flex items-center gap-2">
            <Award className="w-4 h-4" /> {t('ao_staff_section')} ({wonToStaff.length})
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {wonToStaff.map(c => {
              // 04/02/2026 — Q1 Bug B : afficher le délai de staffing
              // restant et un avertissement visuel quand on approche.
              const daysLeft = typeof c.tenderStaffDeadline === 'number'
                ? Math.max(0, c.tenderStaffDeadline - totalDays)
                : null;
              const isCritical = daysLeft !== null && daysLeft <= 2;
              const isWarning = daysLeft !== null && daysLeft <= 5 && !isCritical;
              return (
              <div key={c.instanceId} className={`bg-ink-800 border rounded-lg p-4 flex items-center justify-between gap-3 ${
                isCritical ? 'border-rose-500/60 ring-1 ring-rose-500/30'
                : isWarning ? 'border-amber-500/50'
                : 'border-emerald-500/40'
              }`} data-testid={`tender-staff-${c.instanceId}`}>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-foreground truncate">{c.name}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    {c.client} · {c.duration} j · <span className="font-mono text-money">{(c.dailyRevenue || 0).toLocaleString('fr-FR')} €/j</span>
                  </p>
                  {daysLeft !== null ? (
                    <p className={`text-[10px] mt-0.5 font-mono font-bold ${
                      isCritical ? 'text-rose-300' : isWarning ? 'text-amber-300' : 'text-slate-500'
                    }`} data-testid={`tender-staff-deadline-${c.instanceId}`}>
                      {t(isCritical ? 'ao_staff_deadline_critical' : 'ao_staff_deadline', {
                        days: daysLeft,
                        plural: daysLeft > 1 ? 's' : '',
                      })}
                    </p>
                  ) : (
                    <p className="text-[10px] text-slate-500 mt-0.5 italic">{t('ao_staff_hint')}</p>
                  )}
                </div>
                <button
                  onClick={() => setStaffTargetId(c.instanceId)}
                  className={`flex-shrink-0 text-xs font-bold px-3 py-2 rounded-lg border transition-colors flex items-center gap-1.5 ${
                    isCritical
                      ? 'bg-rose-500/20 border-rose-400/50 text-rose-100 hover:bg-rose-500/30'
                      : 'bg-emerald-500/15 border-emerald-400/40 text-emerald-200 hover:bg-emerald-500/25'
                  }`}
                  data-testid={`tender-staff-assign-${c.instanceId}`}
                >
                  <Truck className="w-3.5 h-3.5" /> {t('ao_staff_btn')}
                </button>
              </div>
              );
            })}
          </div>
        </section>
      )}

      <section>
        <h2 className="text-sm uppercase tracking-widest text-slate-400 font-mono mb-3">
          {t('ao_section_open')} ({activeTenders.length})
        </h2>
        {aggressionActive && (
          <div
            className="mb-3 p-3 rounded-lg bg-orange-500/10 border border-orange-500/40 text-orange-200 flex items-start gap-2"
            data-testid="tender-aggression-banner"
          >
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div className="text-xs leading-relaxed">
              <p className="font-bold mb-0.5">{t('ao_aggression_title')} · {aggressionDaysLeft} {t('ao_aggression_days_left')}</p>
              <p className="text-orange-200/80">{t('ao_aggression_body')}</p>
            </div>
          </div>
        )}
        {activeTenders.length === 0 && <EmptyBoard t={t} />}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="tender-board">
          {activeTenders.map(tender => {
            const application = activeApplications.find(a => a.tenderId === tender.id);
            return (
              <TenderCard
                key={tender.instanceId}
                tender={tender}
                application={application}
                t={t}
                onApply={() => setOpenTenderId(tender.instanceId)}
                onAcceptCounter={() => acceptCounter(application.id)}
                onRejectCounter={() => rejectCounter(application.id)}
              />
            );
          })}
        </div>
      </section>

      {tenderHistory.length > 0 && (
        <section className="mt-8">
          <h2 className="text-sm uppercase tracking-widest text-slate-400 font-mono mb-3">
            {t('ao_section_history')}
          </h2>
          <div className="space-y-1.5" data-testid="tender-history">
            {tenderHistory.slice(0, 10).map((h, i) => (
              <div
                key={`${h.tenderId}-${h.resolvedDay}-${i}`}
                className={`flex items-center justify-between text-xs px-3 py-2 rounded border
                  ${h.outcome === 'won'
                    ? 'bg-emerald-950/30 border-emerald-500/30 text-emerald-200'
                    : 'bg-rose-950/30 border-rose-500/30 text-rose-200'}`}
                data-testid={`tender-history-item-${i}`}
              >
                <span className="flex items-center gap-2">
                  {h.outcome === 'won'
                    ? <CheckCircle2 className="w-3.5 h-3.5" />
                    : <AlertTriangle className="w-3.5 h-3.5" />}
                  {/* i18n-keep — proper noun */}
                  <strong>{h.clientName}</strong>
                  <span className="text-slate-500">— J{h.resolvedDay}</span>
                </span>
                <span className="font-mono">
                  {h.outcome === 'won' ? t('ao_history_won') : t('ao_history_lost')}
                  {h.viaCounter ? ` ${t('ao_history_via_negotiation')}` : ''}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {openTender && (
        <TenderApplicationModal
          tender={openTender}
          intel={intel}
          onClose={handleClose}
          onSubmit={handleSubmit}
          computeProbability={computeProbability}
          computeProbabilityRange={computeProbabilityRange}
        />
      )}

      {staffTarget && (
        <ContractAssignModal
          contract={staffTarget}
          onClose={() => setStaffTargetId(null)}
        />
      )}
    </div>
  );
}

function LockedView({ t }) {
  return (
    <div className="bg-ink-800 rounded-2xl p-8 border border-white/5 max-w-2xl mx-auto" data-testid="ao-locked">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-3 rounded-lg bg-slate-700/40">
          <Lock className="w-6 h-6 text-slate-400" />
        </div>
        <div>
          <h2 className="text-xl font-display font-bold text-slate-200">{t('ao_locked_title')}</h2>
          <p className="text-sm text-slate-500 mt-0.5">{t('ao_locked_subtitle')}</p>
        </div>
      </div>
      <div className="text-sm text-slate-400 leading-relaxed">
        <MarkdownLite text={t('ao_locked_body')} />
      </div>
      <ul className="mt-3 text-xs text-slate-400 space-y-1.5 list-none pl-0">
        <li className="flex items-start gap-2">
          <span className="text-emerald-400 mt-0.5">▸</span>
          <span><MarkdownLite text={t('ao_locked_bullet_junior')} /></span>
        </li>
        <li className="flex items-start gap-2">
          <span className="text-emerald-400 mt-0.5">▸</span>
          <span><MarkdownLite text={t('ao_locked_bullet_senior')} /></span>
        </li>
        <li className="flex items-start gap-2">
          <span className="text-emerald-400 mt-0.5">▸</span>
          <span><MarkdownLite text={t('ao_locked_bullet_expert')} /></span>
        </li>
      </ul>
      <p className="mt-4 text-xs text-slate-500 italic">
        <MarkdownLite as="span" text={t('ao_locked_hint')} />
      </p>
    </div>
  );
}

function EligibilitySummary({ elig, t }) {
  return (
    <div className="bg-ink-700/40 rounded-lg px-3 py-2 border border-white/5 text-xs" data-testid="ao-eligibility">
      <div className="flex items-center gap-4">
        <span className="text-slate-400 flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" /> {t('ao_eligibility_commercial')}</span>
        <strong className="text-emerald-300 uppercase">{elig.commercialLevel || '—'}</strong>
      </div>
      <div className="flex items-center gap-4 mt-1">
        <span className="text-slate-400 flex items-center gap-1"><Truck className="w-3.5 h-3.5" /> {t('ao_eligibility_fleet')}</span>
        <strong className="text-slate-200">{elig.fleetSize}</strong>
        <span className="text-slate-500">·</span>
        <span className="text-slate-400 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> {t('ao_eligibility_rse')}</span>
        <strong className="text-emerald-300 capitalize">{elig.rseTier}</strong>
        <span className="text-slate-500">·</span>
        <span className="text-slate-400">{t('ao_eligibility_medical')}</span>
        <strong className={elig.medicalPct >= 90 ? 'text-emerald-300' : 'text-amber-300'}>{elig.medicalPct}%</strong>
      </div>
    </div>
  );
}

function EmptyBoard({ t }) {
  return (
    <div className="bg-ink-700/30 rounded-xl p-6 border border-white/5 text-center" data-testid="tender-empty">
      <Hourglass className="w-8 h-8 mx-auto text-slate-500 mb-2" />
      <p className="text-sm text-slate-400">{t('ao_empty_title')}</p>
      <div className="text-xs text-slate-500 mt-1">
        <MarkdownLite text={t('ao_empty_hint')} />
      </div>
    </div>
  );
}

function TenderCard({ tender, application, t, onApply, onAcceptCounter, onRejectCounter }) {
  const tone = TIER_TONE[tender.tier] || TIER_TONE.local;
  const totalDays = useGameStore(s => s.totalDays || 0);
  const isSpot = !!tender.spot;

  return (
    <div
      className={`bg-ink-800 rounded-xl p-4 border ${isSpot ? `${SPOT_TONE.cardBorder} ${SPOT_TONE.cardHover}` : 'border-white/10 hover:border-emerald-500/30'} transition-colors`}
      data-testid={`tender-card-${tender.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-base font-display font-bold text-slate-100 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-slate-400" />
            {tender.clientName/* i18n-keep — proper noun */}
          </h3>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider mt-0.5">{tender.sector/* i18n-keep — taxonomy slug */}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wider ${tone.bg} ${tone.text} border ${tone.border}`}>
            {t(tone.labelKey)}
          </span>
          {isSpot && (
            <span
              className={`px-2 py-0.5 rounded-full text-[10px] font-mono font-bold uppercase tracking-wider ${SPOT_TONE.bg} ${SPOT_TONE.text} border ${SPOT_TONE.border}`}
              data-testid={`tender-spot-badge-${tender.id}`}
              title={t('ao_spot_badge_tooltip')}
            >
              {t('ao_spot_badge')}
            </span>
          )}
        </div>
      </div>

      <p className="text-xs text-slate-400 leading-relaxed mb-3">
        {tender.narrative.announce/* i18n-keep — narrative strings stay FR (game lore consistent with Messagerie memos) */}
      </p>

      <div className="grid grid-cols-2 gap-2 text-[11px] mb-3">
        {isSpot ? (
          <>
            <Stat icon={Coins} label={t('ao_spot_stat_payout')} value={t('ao_spot_payout_range', { min: tender.callPayoutRange[0], max: tender.callPayoutRange[1] })} tone="emerald" />
            <Stat icon={Clock} label={t('ao_spot_stat_deadline')} value={t('ao_spot_deadline_value', { n: tender.responseDeadlineDays })} tone="amber" />
            <Stat icon={Truck} label={t('ao_spot_stat_calls_per_month')} value={t('ao_spot_calls_range', { min: tender.callsPerMonthMin, max: tender.callsPerMonthMax })} />
            <Stat icon={Award} label={t('ao_spot_stat_engagement')} value={t('ao_spot_engagement_days', { n: tender.engagementDays || 90 })} />
          </>
        ) : (
          <>
            <Stat icon={Coins} label={t('ao_card_ref_per_day')} value={`${tender.baseDailyRevenue} €`} tone="emerald" />
            <Stat icon={Clock} label={t('ao_card_duration')} value={`${tender.durationDays} j`} />
            <Stat icon={Truck} label={t('ao_card_min_fleet')} value={t('ao_card_fleet_count', { n: tender.requiresFleet })} />
            <Stat icon={Award} label={t('ao_card_min_rse')} value={tender.requiresRseTier === 'none' ? '—' : tender.requiresRseTier} />
          </>
        )}
      </div>

      {!application && (
        <>
          <div className="text-[10px] text-slate-500 italic mb-2">
            {t('ao_card_expires_in', { days: Math.max(0, (tender.expiresOn || 0) - totalDays) })}
          </div>
          <button
            type="button"
            onClick={onApply}
            data-testid={`tender-apply-${tender.id}`}
            className="w-full py-2 rounded bg-emerald-600/20 text-emerald-300 border border-emerald-500/40 text-sm font-semibold hover:bg-emerald-600/30 transition"
          >
            {t('ao_card_prepare_offer')}
          </button>
        </>
      )}

      {application?.stage === 'pending' && (
        <div className="bg-sky-950/40 border border-sky-500/30 rounded p-2.5 text-xs text-sky-200"
             data-testid={`tender-pending-${tender.id}`}>
          <p className="flex items-center gap-1.5 mb-1">
            <Hourglass className="w-3.5 h-3.5" />
            {t('ao_card_pending')}
          </p>
          <p className="text-[10px] text-sky-300">
            {t('ao_card_pending_detail', {
              price: application.originalOffer?.dailyPrice ?? '—',
              duration: application.originalOffer?.durationDays ?? '—',
              day: application.decisionDay,
            })}
          </p>
          <p className="text-[10px] text-slate-400 italic mt-1">
            {t('ao_card_probability_estimate', { pct: Math.round((application.probability ?? 0) * 100) })}
          </p>
        </div>
      )}

      {application?.stage === 'counter' && application.counterOffer && (
        <div className="bg-amber-950/40 border border-amber-500/40 rounded p-3 text-xs space-y-2"
             data-testid={`tender-counter-${tender.id}`}>
          <p className="text-amber-200 flex items-center gap-1.5 font-semibold">
            <Target className="w-3.5 h-3.5" /> {t('ao_card_counter_received')}
          </p>
          <div className="grid grid-cols-2 gap-2 text-[11px]">
            <div className="bg-ink-800/60 rounded p-1.5">
              <p className="text-slate-500">{t('ao_card_your_offer')}</p>
              <p className="text-slate-300 font-mono">{application.originalOffer?.dailyPrice ?? '—'} €/j</p>
            </div>
            <div className="bg-emerald-950/30 rounded p-1.5 border border-emerald-500/30">
              <p className="text-emerald-300">{t('ao_card_counter_offer')}</p>
              <p className="text-emerald-200 font-mono font-bold">{application.counterOffer.dailyPrice} €/j</p>
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onAcceptCounter}
              data-testid={`tender-accept-counter-${tender.id}`}
              className="flex-1 py-1.5 rounded bg-emerald-600/30 text-emerald-200 border border-emerald-500/50 text-xs font-semibold hover:bg-emerald-600/40"
            >
              {t('ao_card_accept')}
            </button>
            <button
              type="button"
              onClick={onRejectCounter}
              data-testid={`tender-reject-counter-${tender.id}`}
              className="flex-1 py-1.5 rounded bg-rose-600/20 text-rose-200 border border-rose-500/40 text-xs font-semibold hover:bg-rose-600/30"
            >
              {t('ao_card_refuse')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, tone }) {
  return (
    <div className="bg-ink-700/30 rounded p-1.5 border border-white/5">
      <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-slate-500">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <p className={`font-mono text-xs font-semibold ${tone === 'emerald' ? 'text-emerald-300' : tone === 'amber' ? 'text-amber-300' : 'text-slate-200'}`}>
        {value}
      </p>
    </div>
  );
}
